import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectCartItems, clearCart } from '../store/slices/cartSlice';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import api, { getStorageUrl } from '../services/api';
import Layout from '../components/Layout';
import './Order.css';

const Order = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector(selectCartItems);
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);

  const [orderItems] = useState(() => {
    const buyNowItems = location.state?.buyNowItems;
    const buyNowItem = location.state?.buyNowItem;

    if (buyNowItems?.length > 0) return buyNowItems;
    if (buyNowItem) return [{ ...buyNowItem, quantity: buyNowItem.quantity || 1 }];
    return cartItems;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (orderItems.length === 0) {
      navigate('/products');
    }
  }, [orderItems.length, navigate]);

  const hasLocalItems = orderItems.some((item) => item.availability_type === 'available_locally');
  const hasNonLocalItems = orderItems.some((item) => item.availability_type !== 'available_locally');

  const calculateTotal = () => {
    const subtotalRmb = orderItems.reduce((sum, item) => {
      return sum + ((item.price || 0) * item.quantity);
    }, 0);
    const shippingNaira = 0; // No shipping fee
    return {
      subtotalRmb,
      shippingNaira,
      totalRmb: subtotalRmb,
    };
  };

  const totals = calculateTotal();
  const displaySubtotal = toDisplayNaira(totals.subtotalRmb, nairaPerYuan);
  const displayTotal = displaySubtotal; // total = subtotal (no shipping fee)

  const formatPrice = (priceInYuan) => {
    return formatNaira(toDisplayNaira(priceInYuan, nairaPerYuan));
  };

  const handlePlaceOrder = async () => {
    if (orderItems.length === 0 || submitting) return;
    if (!nairaPerYuan || nairaPerYuan <= 0) {
      setError('Currency rate is not loaded. Please refresh and try again.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const itemsPayload = orderItems.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
        size_id: item.sizeId || null,
      }));

      const payload = {
        items: itemsPayload,
        currency_rate: nairaPerYuan,
      };

      const response = await api.post('/orders', payload);
      const createdOrder = response.data.order;

      // Clear cart after successful order
      dispatch(clearCart());

      // Redirect to checkout page with order info
      navigate('/checkout', {
        state: {
          orderId: createdOrder.id,
          orderNumber: createdOrder.order_number,
          total: displayTotal,
        },
      });
    } catch (err) {
      console.error('Failed to place order', err);
      setError(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (orderItems.length === 0) {
    return null;
  }

  return (
    <Layout>
      <div className="order-page">
        <div className="order-container">
          <h1>Order</h1>

          <div className="order-content">
            <div className="order-left">
              <section className="order-section">
                <h2>Order Information</h2>
                {hasLocalItems && (
                  <p>
                    After payment, our team will contact you to arrange the waybill and send your locally available
                    item{orderItems.filter((item) => item.availability_type === 'available_locally').length > 1 ? 's' : ''}
                    {' '}to your location.
                  </p>
                )}
                {hasNonLocalItems && (
                  <p>
                    Your items shipped from China will be delivered to our Nigeria warehouse. Our team will contact
                    you when they are ready for pickup or local delivery.
                  </p>
                )}
              </section>
            </div>

            {/* Order Summary */}
            <div className="order-right">
              <div className="order-summary">
                <h2>Order Summary</h2>
                {error && <p className="order-error-message">{error}</p>}

                <div className="order-items">
                  {orderItems.map((item) => (
                    <div key={item.id} className="order-item">
                      <div className="order-item-image">
                        <img src={getStorageUrl(item.image || item.image_path)} alt={item.name} />
                      </div>
                      <div className="order-item-details">
                        <h3>{item.name}</h3>
                        <p>Quantity: {item.quantity}</p>
                        <p className="order-item-price">
                          {formatPrice((item.price || 0) * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-totals">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>{formatNaira(displaySubtotal)}</span>
                  </div>
                  <div className="total-row total">
                    <span>Total:</span>
                    <span>{formatNaira(displayTotal)}</span>
                  </div>
                </div>

                <button
                  onClick={handlePlaceOrder}
                  className="place-order-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Order;

